<template>
  <v-container class="referrals-page py-8">
    <div class="d-flex flex-wrap justify-space-between align-center mb-6">
      <div>
        <div class="text-overline primary--text">
          GROW YOUR CIRCLE
        </div><h1 class="text-h4 mb-2">
          Referrals &amp; affiliates
        </h1><p class="mb-0">
          Help someone start preserving their story.
        </p>
      </div>
      <v-btn
        v-if="data && data.admin"
        to="/admin/affiliates"
        outlined
        color="primary"
      >
        Administration
      </v-btn>
    </div>
    <v-alert
      v-if="error"
      type="error"
      outlined
    >
      {{ error }} <v-btn
        text
        small
        @click="load"
      >
        Try again
      </v-btn>
    </v-alert>
    <v-alert
      v-if="message"
      type="success"
      outlined
      dismissible
    >
      {{ message }}
    </v-alert>
    <v-progress-linear
      v-if="loading"
      indeterminate
      aria-label="Loading referrals"
    ></v-progress-linear>
    <template v-if="data">
      <v-card
        v-if="!data.affiliate"
        outlined
        class="pa-6 mb-6"
      >
        <div class="text-overline primary--text">
          PARTNER PROGRAM
        </div><h2 class="text-h5 mb-3">
          Become an affiliate
        </h2>
        <p>Earn {{ data.rules.commission_bps / 100 }}% on all eligible purchases, including subscriptions, renewals, upgrades and AI credit packs, for {{ data.rules.commission_months }} months from each customer's first purchase. No referral limit.</p>
        <p>Your attribution also follows friends invited by your customers, at every level. Commissions go to the originating affiliate. Customers joining through your affiliate link receive {{ data.rules.friend_credits }} AI credits after their first subscription payment and the {{ data.rules.hold_days }}-day validation period.</p>
        <v-textarea
          v-model="application"
          label="How will you promote Self Biography?"
          outlined
          counter="2000"
          :maxlength="2000"
          rows="3"
        ></v-textarea>
        <v-btn
          color="primary"
          :disabled="application.trim().length < 10"
          :loading="busy"
          @click="apply"
        >
          Apply to become an affiliate
        </v-btn>
      </v-card>
      <template v-else>
        <v-card
          outlined
          class="pa-6 mb-6"
        >
          <div class="d-flex flex-wrap align-center mb-3">
            <h2 class="text-h5 mr-3">
              Your affiliate account
            </h2><v-chip
              small
              :color="data.affiliate.status === 'active' ? 'success' : 'warning'"
              outlined
            >
              {{ data.affiliate.status }}
            </v-chip>
          </div>
          <p>Customers joining through your affiliate link receive {{ data.rules.friend_credits }} AI credits under the same validation and expiration rules as friend invitations. You earn commission on all eligible purchases, including AI credit packs.</p>
          <p v-if="data.affiliate.status === 'pending'">
            Your application is awaiting review. Your affiliate link becomes available after approval.
          </p>
          <p v-else-if="data.affiliate.status !== 'active'">
            Your affiliate link and payouts are unavailable. Contact support for a review.
          </p>
          <template v-else>
            <v-text-field
              :value="data.affiliate.link"
              label="Your affiliate link"
              readonly
              outlined
              hide-details
              class="mb-3"
            ></v-text-field><v-btn
              color="primary"
              @click="copy(data.affiliate.link)"
            >
              Copy affiliate link
            </v-btn>
          </template>
          <p class="text-caption mt-4 mb-0">
            {{ data.rules.commission_bps / 100 }}% for new attributed customers · {{ data.rules.commission_months }} months · {{ data.rules.hold_days }}-day release · {{ money(data.rules.payout_minimum) }} minimum payout. Terms are fixed when each customer joins.
          </p>
        </v-card>
        <v-row class="mb-5">
          <v-col
            v-for="stat in affiliateStats"
            :key="stat.label"
            cols="6"
            md="3"
          >
            <v-card
              outlined
              class="pa-5"
            >
              <div class="text-body-2 mb-2">
                {{ stat.label }}
              </div><strong class="text-h4">{{ stat.value }}</strong>
            </v-card>
          </v-col>
        </v-row>
        <v-card
          outlined
          class="mb-6"
        >
          <v-card-title>Recent commissions</v-card-title><v-card-subtitle>Your latest 100 purchases, including subscriptions, AI credit packs and inherited referrals.</v-card-subtitle>
          <v-data-table
            :headers="commissionHeaders"
            :items="data.affiliate.commissions"
            item-key="invoice_id"
            :items-per-page="10"
            no-data-text="Your commissions will appear after a referred customer pays."
          >
            <template v-slot:item.payment_kind="{ item }">
              {{ item.payment_kind === 'purchase' ? 'One-time purchase' : 'Subscription' }}
            </template>
            <template v-slot:item.paid_at="{ item }">
              {{ date(item.paid_at) }}
            </template>
            <template v-slot:item.commission_amount="{ item }">
              {{ money(item.commission_amount) }}
            </template>
            <template v-slot:item.available_at="{ item }">
              {{ date(item.available_at) }}
            </template>
            <template v-slot:item.status="{ item }">
              {{ commissionStatus(item) }}
            </template>
          </v-data-table>
        </v-card>
        <v-card
          outlined
          class="mb-6"
        >
          <v-card-title>Payout history</v-card-title><v-data-table
            :headers="payoutHeaders"
            :items="data.affiliate.payouts"
            item-key="id"
            :items-per-page="10"
            no-data-text="No payouts yet."
          >
            <template v-slot:item.amount="{ item }">
              {{ money(item.amount) }}
            </template><template v-slot:item.created_at="{ item }">
              {{ date(item.created_at) }}
            </template>
          </v-data-table>
        </v-card>
      </template>
      <v-card
        outlined
        class="pa-6 mb-6 referral-hero"
      >
        <div class="text-overline primary--text">
          INVITE A FRIEND
        </div>
        <h2 class="text-h5 mb-3">
          {{ data.rules.friend_credits }} AI credits for each of you
        </h2>
        <p>Share your link with a new member. After their first subscription payment and a {{ data.rules.hold_days }}-day validation period, you both receive credits. There is no invitation limit.</p>
        <v-text-field
          :value="data.friendLink"
          label="Your invitation link"
          readonly
          outlined
          hide-details
          class="mb-3"
        ></v-text-field>
        <v-btn
          color="primary"
          @click="copy(data.friendLink)"
        >
          Copy invitation link
        </v-btn>
        <p class="text-caption mt-3 mb-0">
          Links apply to signups within 30 days. Your friend must subscribe within 90 days of signup. Reward credits expire {{ data.rules.credit_expiry_days }} days after release. Existing invitations retain their original terms.
        </p>
      </v-card>
      <v-row class="mb-5">
        <v-col
          v-for="stat in friendStats"
          :key="stat.label"
          cols="6"
          md="3"
        >
          <v-card
            outlined
            class="pa-5"
          >
            <div class="text-body-2 mb-2">
              {{ stat.label }}
            </div><strong class="text-h4">{{ stat.value }}</strong>
          </v-card>
        </v-col>
      </v-row>
    </template>
  </v-container>
</template>

<script>
import referrals from '@/services/referrals'

export default {
  name: 'Referrals',
  data: () => ({
    data: null,
    loading: false,
    busy: false,
    error: '',
    message: '',
    application: '',
    commissionHeaders: [{ text: 'Purchase', value: 'invoice_id' }, { text: 'Type', value: 'payment_kind' }, { text: 'Payment date', value: 'paid_at' }, { text: 'Commission', value: 'commission_amount' }, { text: 'Release date', value: 'available_at' }, { text: 'Status', value: 'status', sortable: false }],
    payoutHeaders: [{ text: 'Date', value: 'created_at' }, { text: 'Amount', value: 'amount' }, { text: 'Status', value: 'status' }, { text: 'Reference', value: 'payment_reference' }],
  }),
  computed: {
    friendStats() {
      const credits = status => {
        const row = this.data.friend.rewards.find(r => r.status === status)

        return row ? Number(row.credits) : 0
      }

      return [{ label: 'Friends invited', value: this.data.friend.invited }, { label: 'Subscriptions started', value: this.data.friend.converted }, { label: 'Pending credits', value: credits('pending') }, { label: 'Credits awarded', value: credits('granted') }]
    },
    affiliateStats() {
      const a = this.data.affiliate

      return [{ label: 'Attributed members', value: a.network }, { label: 'Pending commission', value: this.money(a.totals.pending) }, { label: 'Available balance', value: this.money(a.totals.available) }, { label: 'Paid out', value: this.money(a.totals.paid) }]
    },
  },
  mounted() { this.load() },
  methods: {
    money(value) { return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format((value || 0) / 100) },
    date(value) { return value ? new Date(value).toLocaleDateString() : '—' },
    commissionStatus(item) {
      if (item.disputed) return 'Under review'
      if (item.refunded_amount) return 'Adjusted after refund'
      if (item.reserved_payout_id) return 'Payout prepared'
      if (item.settled_amount >= item.commission_amount) return 'Settled'

      return new Date(item.available_at) > new Date() ? 'Pending' : 'Available'
    },
    async load() {
      this.loading = true; this.error = ''
      try { this.data = await referrals.me() } catch (error) { this.error = error.response ? (error.response.data.message || 'Referrals are temporarily unavailable.') : error.message } finally { this.loading = false }
    },
    async copy(value) {
      try { await navigator.clipboard.writeText(value); this.message = 'Link copied.' } catch (_) { this.error = 'Select the link above and copy it manually.' }
    },
    async apply() {
      this.busy = true; this.error = ''
      try { await referrals.apply(this.application); this.message = 'Application submitted for review.'; await this.load() } catch (error) { this.error = (error.response && error.response.data.message) || 'Could not submit your application.' } finally { this.busy = false }
    },
  },
}
</script>

<style scoped>
.referrals-page { max-width: 1180px; }
.referral-hero { border-top: 3px solid var(--v-primary-base) !important; }
</style>
